import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';

/**
 * ProductCard - Modern ve yeniden kullanılabilir ürün kartı bileşeni
 * 
 * @param {Object} props
 * @param {Object} props.product - Ürün objesi
 * @param {string} props.viewMode - Görünüm modu ('grid', 'slider', 'list')
 * @param {boolean} props.showQuickView - Hızlı görünüm butonu gösterilsin mi?
 * @param {boolean} props.compact - Kompakt mod (daha küçük)
 * @param {function} props.onQuickView - Hızlı görünüm callback
 * @param {string} props.className - Ek CSS sınıfları
 */
const ProductCard = ({
  product,
  viewMode = 'grid',
  showQuickView = false,
  compact = false,
  onQuickView,
  className = ''
}) => {
  const { addToCart } = useCart();
  const [isAdded, setIsAdded] = useState(false);

  // Görsel URL
  const imageApiUrl = (process.env.REACT_APP_IMAGE_API_URL || process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');
  const imageUrl = product.imageUrl
    ? (product.imageUrl.startsWith('http')
        ? product.imageUrl
        : `${imageApiUrl}${product.imageUrl}`)
    : "assets/images/logo/logo.png";

  // İndirim hesaplama
  const hasDiscount = product.discountedPrice && product.discountedPrice < product.price;
  const discountPercentage = hasDiscount 
    ? Math.round((1 - product.discountedPrice / product.price) * 100) 
    : 0;
  const currentPrice = product.discountedPrice || product.price;

  // Sepete ekle
  const handleAddToCart = async () => {
    if (isAdded) return;

    try {
      setIsAdded(true);
      
      // Gram bazlı ürünler için 1kg (1000gr), adet bazlı için null
      const weightGrams = product.saleType === 1 ? 1000 : null;
      
      // weightGrams parametresi ile sepete ekle
      const result = await addToCart(
        product.id, 
        1, // quantity (adet olarak 1)
        "", // note
        weightGrams // weightGrams (gram cinsinden)
      );
      
      // 2 saniye sonra butonu normal haline getir
      setTimeout(() => {
        setIsAdded(false);
      }, 2000);
      
      if (!result.success) {
        console.error('Ürün sepete eklenirken hata oluştu:', result.message);
      }
    } catch (error) {
      console.error('Sepete ekleme hatası:', error);
      setIsAdded(false);
    }
  };

  // Hızlı görünüm
  const handleQuickView = (e) => {
    e.preventDefault();
    if (onQuickView) {
      onQuickView(product);
    }
  };


  // Görsel hata durumu
  const handleImageError = (e) => {
    e.target.onerror = null;
    e.target.src = "assets/images/logo/logo.png";
    if (compact) {
      e.target.style.maxWidth = "80px";
      e.target.style.maxHeight = "80px";
    } else {
      e.target.style.maxWidth = "120px";
      e.target.style.maxHeight = "120px";
    }
  };

  // Responsive image height
  const imageHeight = compact 
    ? 'clamp(150px, 25vw, 180px)' 
    : 'clamp(180px, 30vw, 240px)';

  return (
    <div className={`modern-product-card bg-white rounded-20 overflow-hidden shadow-sm h-100 transition-3 hover-shadow-xl ${className}`} style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Product Image Container */}
      <div className="product-image-container position-relative overflow-hidden">
        <Link
          to={`/product-details/${product.slug || product.id}`}
          className="product-image-link d-block position-relative"
          style={{ height: imageHeight }}
        >
          {/* Badges */}
          <div className="product-badges position-absolute top-0 start-0 p-16 z-2">
            {product.isNew && (
              <span className="badge bg-primary text-white px-12 py-6 rounded-8 mb-8 d-block fw-semibold" style={{ fontSize: '12px' }}>
                YENİ
              </span>
            )}
            {hasDiscount && (
              <span className="badge bg-danger text-white px-12 py-6 rounded-8 fw-semibold" style={{ fontSize: '12px' }}>
                %{discountPercentage} İNDİRİM
              </span>
            )}
            {product.isOutOfStock && (
              <span className="badge bg-secondary text-white px-12 py-6 rounded-8 mt-8 d-block fw-semibold" style={{ fontSize: '12px' }}>
                STOKTA YOK
              </span>
            )}
          </div>

          {/* Quick View Button */}
          {showQuickView && !product.isOutOfStock && (
            <button
              onClick={handleQuickView}
              className="quick-view-btn position-absolute top-0 end-0 m-16 bg-white rounded-circle shadow-sm border-0 p-12 z-2 transition-2 hover-bg-main-600 hover-text-white"
              style={{ width: '40px', height: '40px' }}
              title="Hızlı Görünüm"
            >
              <i className="ph ph-eye text-18"></i>
            </button>
          )}

          {/* Product Image */}
          <div className="product-image-wrapper h-100 bg-light d-flex align-items-center justify-content-center">
            <img
              src={imageUrl}
              alt={product.name}
              className="product-main-image w-100 h-100 object-fit-cover transition-3"
              style={{ maxHeight: imageHeight }}
              onError={handleImageError}
              loading="lazy"
            />
          </div>
        </Link>
      </div>

      {/* Product Content */}
      <div className={`product-content ${compact ? 'p-12' : 'p-20'}`} style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        {/* Product Title */}
        <h6 className={`product-title ${compact ? 'mb-8' : 'mb-12'}`}>
          <Link 
            to={`/product-details/${product.slug || product.id}`} 
            className="text-decoration-none text-dark fw-semibold text-line-2"
            style={{ 
              fontSize: compact ? 'clamp(12px, 2.5vw, 14px)' : 'clamp(14px, 3vw, 16px)', 
              lineHeight: '1.4' 
            }}
          >
            {product.name}
          </Link>
        </h6>

        {/* Rating */}
        <div className={`product-rating d-flex align-items-center gap-4 ${compact ? 'mb-8' : 'mb-16'}`}>
          <div className="stars d-flex gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <i 
                key={star}
                className={`ph-fill ph-star ${star <= Math.floor(product.rating || 0) ? 'text-warning' : 'text-gray-300'}`}
                style={{ fontSize: compact ? '12px' : '14px' }}
              ></i>
            ))}
          </div>
          <span className="rating-text text-gray-600" style={{ fontSize: compact ? '11px' : '13px' }}>
            {product.rating ? product.rating.toFixed(1) : '0.0'} ({product.reviewCount || 0})
          </span>
        </div>

        {/* Price */}
        <div className={`product-price ${compact ? 'mb-12' : 'mb-20'}`}>
          {hasDiscount ? (
            <div className="price-with-discount">
              <div className="d-flex align-items-center gap-8">
                <span className="current-price text-main-600 fw-bold" style={{ fontSize: compact ? 'clamp(14px, 3vw, 16px)' : 'clamp(16px, 4vw, 20px)' }}>
                  ₺{currentPrice.toFixed(2)}
                </span>
                <span className="original-price text-gray-400 text-decoration-line-through" style={{ fontSize: compact ? 'clamp(10px, 2vw, 12px)' : 'clamp(12px, 2.5vw, 14px)' }}>
                  ₺{product.price.toFixed(2)}
                </span>
              </div>
              <span className="price-unit text-gray-500" style={{ fontSize: compact ? 'clamp(9px, 2vw, 11px)' : 'clamp(11px, 2.5vw, 13px)' }}>
                / {product.saleType === 1 ? 'Kg' : 'Adet'}
              </span>
            </div>
          ) : (
            <div className="price-normal">
              <span className="current-price text-main-600 fw-bold" style={{ fontSize: compact ? 'clamp(14px, 3vw, 16px)' : 'clamp(16px, 4vw, 20px)' }}>
                ₺{currentPrice.toFixed(2)}
              </span>
              <span className="price-unit text-gray-500 ms-4" style={{ fontSize: compact ? 'clamp(9px, 2vw, 11px)' : 'clamp(11px, 2.5vw, 13px)' }}>
                / {product.saleType === 1 ? 'Kg' : 'Adet'}
              </span>
            </div>
          )}
        </div>

        {/* Stock Info */}
        {product.stockQuantity > 0 && product.stockQuantity <= 10 && !product.isOutOfStock && (
          <div className={`stock-info ${compact ? 'mb-8' : 'mb-12'}`}>
            <div className="d-flex align-items-center gap-4">
              <i className="ph ph-warning text-warning"></i>
              <span className="text-warning text-xs">
                Son {product.stockQuantity} ürün!
              </span>
            </div>
          </div>
        )}

        {/* Add to Cart Button */}
        <button
          onClick={handleAddToCart}
          disabled={isAdded || product.isOutOfStock}
          style={{ 
            marginTop: 'auto', 
            fontSize: compact ? 'clamp(11px, 2.5vw, 13px)' : 'clamp(12px, 3vw, 14px)',
            padding: compact ? 'clamp(8px, 2vw, 10px) clamp(12px, 3vw, 14px)' : 'clamp(10px, 2.5vw, 12px) clamp(14px, 3.5vw, 16px)' 
          }}
          className={`add-to-cart-btn w-100 rounded-12 border-0 fw-semibold transition-2 ${
            isAdded 
              ? 'bg-success text-white' 
              : product.isOutOfStock
              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
              : 'bg-main-600 text-white hover-bg-main-700'
          }`}
        >
          {isAdded ? (
            <>
              <i className="ph ph-check me-8"></i>
              Sepete Eklendi
            </>
          ) : product.isOutOfStock ? (
            <>
              <i className="ph ph-x-circle me-8"></i>
              Stokta Yok
            </>
          ) : (
            <>
              <i className="ph ph-shopping-cart me-8"></i>
              Sepete Ekle
            </>
          )}
        </button>
      </div>

      {/* Styles */}
      <style jsx>{`
        .modern-product-card {
          border: 1px solid #f0f0f0;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .modern-product-card:hover {
          border-color: var(--main-600);
          transform: translateY(-4px);
        }

        .product-image-wrapper img {
          transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .modern-product-card:hover .product-image-wrapper img {
          transform: scale(1.08);
        }

        .quick-view-btn {
          opacity: 0;
          transition: all 0.3s ease;
        }

        .modern-product-card:hover .quick-view-btn {
          opacity: 1;
        }

        .product-badges .badge {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        }

        .transition-3 {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .transition-2 {
          transition: all 0.2s ease;
        }

        .hover-shadow-xl:hover {
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
        }

        .text-line-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .add-to-cart-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(41, 158, 96, 0.3);
        }

        .add-to-cart-btn:disabled {
          cursor: not-allowed;
        }

        .cursor-not-allowed {
          cursor: not-allowed !important;
        }
      `}</style>
    </div>
  );
};

export default ProductCard;

